import { useState, useEffect } from "react";
import { BarChart3, TrendingUp, ExternalLink, Loader2, Info, Mail, CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import LiveBadge from "./LiveBadge";

export default function AnalyticsPanel({ blueprintId, saved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewLink, setPreviewLink] = useState(null);

  useEffect(() => {
    if (!blueprintId || !saved) return;
    load();
    const interval = setInterval(load, 60000); // refresh every 60s while workspace is open
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [blueprintId, saved]);

  const load = async () => {
    if (!blueprintId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/website/projects/${blueprintId}/analytics`);
      setData(data);
    } catch {
      // not saved yet or not owned — silent
    } finally { setLoading(false); }
  };

  const sendDigestPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post(`/website/projects/${blueprintId}/digest/preview`);
      if (data.sent) toast.success("Weekly digest preview sent to your inbox ✉️");
      else if (data.preview_html) {
        // Dev/preview mode with no Resend — open the rendered HTML in a new tab
        const blob = new Blob([data.preview_html], { type: "text/html" });
        setPreviewLink(URL.createObjectURL(blob));
        toast("Digest ready — email not configured, preview opened in new tab.", { duration: 5000 });
      } else {
        toast.error("Could not send digest.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send digest.");
    } finally { setPreviewing(false); }
  };

  if (!blueprintId || !saved) return null;

  const total = data?.total_7d ?? 0;
  const daily = data?.daily || [];
  const maxDaily = Math.max(...daily, 1);
  const today = daily[daily.length - 1] || 0;
  const yesterday = daily[daily.length - 2] || 0;
  const delta = today - yesterday;

  return (
    <div className="card-soft p-4" data-testid="analytics-panel">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="w-4 h-4 text-teal" />
        <h3 className="text-xs font-semibold uppercase tracking-wider text-ink flex-1">Analytics · 7d</h3>
        <LiveBadge blueprintId={blueprintId} saved={saved} />
        {loading && <Loader2 className="w-3 h-3 animate-spin text-ink2" />}
      </div>

      {total === 0 && (
        <div className="text-[11px] text-ink2 leading-relaxed" data-testid="analytics-empty">
          <Info className="w-3 h-3 inline-block mr-1 text-marigold" />
          Deploy your site and the built-in visits counter starts working — no setup, no cookies, no Google Analytics.
        </div>
      )}

      {total > 0 && (
        <div data-testid="analytics-summary">
          <div className="flex items-baseline gap-2 mb-2">
            <div className="font-heading text-3xl font-semibold text-ink" data-testid="analytics-total">{total}</div>
            <div className="text-[10px] uppercase tracking-wider text-ink2 font-semibold">visits</div>
            {delta !== 0 && (
              <div className={`text-[10px] font-semibold ml-auto inline-flex items-center gap-0.5 ${delta > 0 ? "text-teal" : "text-marigold"}`}>
                <TrendingUp className={`w-3 h-3 ${delta < 0 ? "rotate-180" : ""}`} />
                {delta > 0 ? "+" : ""}{delta} today
              </div>
            )}
          </div>

          {/* Sparkline bars */}
          <div className="flex items-end gap-1 h-14 mb-3" data-testid="analytics-sparkline">
            {daily.map((v, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1 justify-end">
                <div
                  className="w-full rounded-t bg-teal/70 hover:bg-teal transition"
                  style={{ height: `${Math.max(2, (v / maxDaily) * 100)}%` }}
                  title={`${v} visits`}
                />
                <div className="text-[9px] text-ink2/70 font-mono">
                  {["7d", "6d", "5d", "4d", "3d", "2d", "1d"][i]}
                </div>
              </div>
            ))}
          </div>

          {data?.top_paths?.length > 0 && (
            <div className="pt-3 border-t border-border">
              <div className="text-[10px] uppercase tracking-wider text-ink2 font-semibold mb-1.5">Top pages</div>
              <ul className="space-y-1" data-testid="analytics-top-paths">
                {data.top_paths.slice(0, 3).map((p) => (
                  <li key={p.path} className="text-xs flex items-center gap-2 justify-between">
                    <span className="text-ink truncate font-mono text-[10px]">{p.path}</span>
                    <span className="text-ink2 flex-shrink-0">{p.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data?.top_referrers?.length > 0 && (
            <div className="pt-3 border-t border-border mt-3">
              <div className="text-[10px] uppercase tracking-wider text-ink2 font-semibold mb-1.5">Top referrers</div>
              <ul className="space-y-1" data-testid="analytics-top-referrers">
                {data.top_referrers.slice(0, 3).map((r) => (
                  <li key={r.host} className="text-xs flex items-center gap-2 justify-between">
                    <span className="text-ink truncate flex items-center gap-1">
                      {r.host !== "direct" && <ExternalLink className="w-2.5 h-2.5 text-ink2 flex-shrink-0" />}
                      {r.host}
                    </span>
                    <span className="text-ink2 flex-shrink-0">{r.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="pt-3 mt-3 border-t border-border/60">
        <button
          onClick={sendDigestPreview}
          disabled={previewing}
          className="w-full text-[11px] px-3 py-2 rounded-lg bg-paper border border-border hover:border-teal transition inline-flex items-center justify-center gap-1.5 text-ink2 hover:text-teal font-medium"
          data-testid="analytics-digest-preview"
        >
          {previewing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
          {previewing ? "Sending…" : "Send me a weekly digest preview"}
        </button>
        {previewLink && (
          <a
            href={previewLink}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-teal hover:underline"
            data-testid="analytics-digest-preview-link"
          >
            <ExternalLink className="w-2.5 h-2.5" /> Open rendered digest
          </a>
        )}
      </div>
    </div>
  );
}
